/** Same-origin tab coordination: logging out in one tab signs the rest out on this origin. */
const CHANNEL = "odp-unified-auth";

/**
 * Cross-tab signal when BroadcastChannel is flaky or unavailable (still same-origin only).
 * Cross-subdomain apps (e.g. VMM vs Insights) rely on /api/v1/auth/status polling instead.
 */
export const ODP_LOGOUT_BROADCAST_STORAGE_KEY = "odp_unified_logout_broadcast_ts";

function getBroadcastChannelCtor() {
  const Ctor = typeof globalThis !== "undefined" ? globalThis.BroadcastChannel : undefined;
  return typeof Ctor === "function" ? Ctor : undefined;
}

export function broadcastAuthLogout() {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(ODP_LOGOUT_BROADCAST_STORAGE_KEY, String(Date.now()));
    }
  } catch {
    // Private mode / blocked storage; BC + polling still apply where possible.
  }
  const Ctor = getBroadcastChannelCtor();
  if (!Ctor) return;
  try {
    const bc = new Ctor(CHANNEL);
    bc.postMessage({ type: "logout", t: Date.now() });
    bc.close();
  } catch {
    // BroadcastChannel unsupported; rely on storage event + polling.
  }
}

/**
 * @param {() => void | Promise<void>} onLogout
 * @returns {() => void} unsubscribe
 */
export function subscribeAuthLogout(onLogout) {
  if (typeof window === "undefined") return () => {};

  const invoke = () => {
    void Promise.resolve(onLogout()).catch(() => {});
  };

  const onStorage = (ev) => {
    if (ev.storageArea !== localStorage) return;
    if (ev.key !== ODP_LOGOUT_BROADCAST_STORAGE_KEY) return;
    if (ev.newValue == null || ev.newValue === "") return;
    invoke();
  };
  window.addEventListener("storage", onStorage);

  let bc;
  const Ctor = getBroadcastChannelCtor();
  if (Ctor) {
    try {
      bc = new Ctor(CHANNEL);
      bc.onmessage = (ev) => {
        if (ev?.data?.type === "logout") invoke();
      };
    } catch {
      // ignore
    }
  }

  return () => {
    window.removeEventListener("storage", onStorage);
    try {
      bc?.close();
    } catch {
      // ignore
    }
  };
}
